import type { Coords } from "@/types/places";
import { z } from "zod";

// Small epsilon for floating-point boundary validation
const COORD_EPSILON = 1e-10;

/**
 * Zod schema for validating geographic coordinates
 * Uses epsilon tolerance for boundary values to handle floating-point precision
 */
export const CoordinateSchema = z.object({
  lat: z.number().refine((val) => val >= -90 - COORD_EPSILON && val <= 90, {
    message: "Latitude must be between -90 and 90 degrees",
  }),
  lon: z
    .number()
    .refine(
      (val) => val >= -180 - COORD_EPSILON && val <= 180 + COORD_EPSILON,
      { message: "Longitude must be between -180 and 180 degrees" },
    ),
});

/**
 * Type-safe coordinate validation
 * @param coords - Coordinates to validate
 * @returns true if coordinates are valid, false otherwise
 */
export function isValidCoordinate(coords: unknown): coords is Coords {
  return CoordinateSchema.safeParse(coords).success;
}

/**
 * Validates and returns coordinates or undefined if invalid
 * @param coords - Coordinates to validate
 * @returns Validated coordinates or undefined
 */
export function validateCoordinates(coords: unknown): Coords | undefined {
  const result = CoordinateSchema.safeParse(coords);
  return result.success ? result.data : undefined;
}

/**
 * Validates individual lat/lon values and throws if invalid.
 * Used by OpenSearch filter builders that require strict validation.
 *
 * @param lat - Latitude value
 * @param lon - Longitude value
 * @throws {Error} If coordinates are invalid (includes type checking and range validation)
 *
 * @example
 * ```typescript
 * try {
 *   assertValidCoordinates(59.9139, 10.7522); // Valid
 *   assertValidCoordinates(100, 10); // Throws error
 * } catch (error) {
 *   // Handle invalid coordinates
 * }
 * ```
 */
export function assertValidCoordinates(
  lat: number,
  lon: number,
): asserts lat is number {
  // Type and finiteness checks before schema validation
  if (
    typeof lat !== "number" ||
    typeof lon !== "number" ||
    !Number.isFinite(lat) ||
    !Number.isFinite(lon)
  ) {
    throw new Error(
      `Invalid coordinate types: lat=${lat} (${typeof lat}), lon=${lon} (${typeof lon})`,
    );
  }

  // Reuse Zod schema validation for range checking with epsilon tolerance
  const result = CoordinateSchema.safeParse({ lat, lon });
  if (!result.success) {
    // Extract the first validation error for a clear message
    const issue = result.error.issues[0];
    const field = issue.path[0] === "lat" ? "latitude" : "longitude";
    const value = issue.path[0] === "lat" ? lat : lon;
    const range = issue.path[0] === "lat" ? "[-90, 90]" : "[-180, 180]";
    throw new Error(
      `Invalid ${field}: ${field.slice(0, 3)}=${value} (must be in ${range})`,
    );
  }
}

/**
 * Checks if coordinates are valid numbers within proper geographic ranges.
 * More strict than isValidCoordinate - also checks for NaN and Infinity.
 *
 * @param location - Coordinate object to validate
 * @returns true if coordinates are finite numbers within valid lat/lon ranges
 */
export function hasValidLocation(location: Coords): boolean {
  return (
    Number.isFinite(location.lat) &&
    Number.isFinite(location.lon) &&
    Math.abs(location.lat) <= 90 &&
    Math.abs(location.lon) <= 180
  );
}

/**
 * Keeps only items whose location is present and geographically valid.
 * The source types declare `location` as required, but ingestion can omit it,
 * so the presence check is load-bearing at runtime, not just a type guard.
 */
export function keepValidlyLocated<T extends { location?: Coords }>(
  items: T[],
): T[] {
  return items.filter(
    (item) => item.location != null && hasValidLocation(item.location),
  );
}
